'use client';

import { reader } from '@woonstad/helpers-utils';
import clsx from 'clsx';
import { forwardRef,useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { BasicBin } from '../../../../icons/basic';
import type { RedactionEditorTexts } from '../maskedDocumentUploadTexts';
import {
  clientPointToImageFraction,
  clientPointToImageFractionClamped,
  getRenderedImageBounds,
} from './imageCoordinates';
import {
  applyResizeDelta,
  clampRect,
  createRedactionRect,
  DEFAULT_RECT_HEIGHT,
  DEFAULT_RECT_WIDTH,
  KEYBOARD_MOVE_STEP,
  MIN_RECT_SIZE,
  rectFromCorners,
  type RedactionRect,
  type ResizeHandle,
} from './redactionRect';

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const TAP_THRESHOLD_PX = 5;
const ZOOM_STEP = 1.5;
const MIN_TOUCH_PX = 44;
const PINCH_GRACE_MS = 250;

type PanState = { x: number; y: number };

type RectInteraction =
  | { type: 'drag'; rectId: string; startX: number; startY: number; rect: RedactionRect }
  | { type: 'resize'; rectId: string; handle: ResizeHandle; startX: number; startY: number; rect: RedactionRect };

type ViewportInteraction =
  | { type: 'pan'; pointerId: number; startPan: PanState; startClient: { x: number; y: number } }
  | { type: 'tap'; pointerId: number; startClient: { x: number; y: number }; moved: boolean }
  | { type: 'draw'; pointerId: number; startClient: { x: number; y: number }; rectId: string | null; moved: boolean };

type PinchStart = {
  distance: number;
  scale: number;
  pan: PanState;
  midpoint: { x: number; y: number };
};

export type RedactionEditorZoomState = {
  canZoomIn: boolean;
  canZoomOut: boolean;
};

export type RedactionEditorHandle = {
  zoomIn: () => void;
  zoomOut: () => void;
};

export type RedactionEditorProps = {
  file: File;
  rectangles: RedactionRect[];
  onRectanglesChange: (rects: RedactionRect[]) => void;
  selectedRectId: string | null;
  onSelectedRectChange: (id: string | null) => void;
  onZoomStateChange?: (state: RedactionEditorZoomState) => void;
  texts: RedactionEditorTexts;
};

function clampScale(value: number): number {
  return Math.max(MIN_SCALE, Math.min(MAX_SCALE, value));
}

function getDistance(
  a: { x: number; y: number },
  b: { x: number; y: number },
): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

function resizeRect(
  rect: RedactionRect,
  handle: ResizeHandle,
  pointerX: number,
  pointerY: number,
  startRect: RedactionRect,
  startPointerX: number,
  startPointerY: number,
): RedactionRect {
  return applyResizeDelta(
    startRect,
    handle,
    pointerX - startPointerX,
    pointerY - startPointerY,
  );
}

type PointerMap = Map<number, { x: number; y: number }>;

function trackActivePointer(pointers: PointerMap, event: React.PointerEvent<HTMLElement>): void {
  if (!pointers.has(event.pointerId)) {
    return;
  }

  pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
}

function pinchPointers(
  pointers: PointerMap,
  pinchStart: PinchStart | null,
  startPinch: () => void,
  updatePinch: () => void,
): boolean {
  if (pointers.size < 2) {
    return false;
  }

  if (!pinchStart) {
    startPinch();
  }

  updatePinch();

  return true;
}

function moveDrawViewport(
  event: React.PointerEvent<HTMLElement>,
  viewportInteractionRef: { current: ViewportInteraction | null },
  updateDrawRect: (
    startClient: { x: number; y: number },
    currentClient: { x: number; y: number },
    rectId: string | null,
  ) => string,
): boolean {
  const viewportInteraction = viewportInteractionRef.current;

  if (
    !viewportInteraction
    || viewportInteraction.pointerId !== event.pointerId
    || viewportInteraction.type !== 'draw'
  ) {
    return false;
  }

  const dx = event.clientX - viewportInteraction.startClient.x;
  const dy = event.clientY - viewportInteraction.startClient.y;
  const moved = Math.hypot(dx, dy) > TAP_THRESHOLD_PX;

  if (moved) {
    const rectId = updateDrawRect(
      viewportInteraction.startClient,
      { x: event.clientX, y: event.clientY },
      viewportInteraction.rectId,
    );

    viewportInteractionRef.current = {
      ...viewportInteraction,
      rectId,
      moved: true,
    };
  }

  return true;
}

type PanMoveContext = {
  viewportInteractionRef: { current: ViewportInteraction | null };
  pan: PanState;
  scale: number;
  setPan: (nextPan: PanState) => void;
  clampPan: (nextPan: PanState, nextScale: number) => PanState;
  onSelectedRectChange: (id: string | null) => void;
};

function movePanOrTapViewport(event: React.PointerEvent<HTMLElement>, context: PanMoveContext): boolean {
  const viewportInteraction = context.viewportInteractionRef.current;

  if (!viewportInteraction || viewportInteraction.pointerId !== event.pointerId) {
    return false;
  }

  const dx = event.clientX - viewportInteraction.startClient.x;
  const dy = event.clientY - viewportInteraction.startClient.y;
  const moved = Math.hypot(dx, dy) > TAP_THRESHOLD_PX;

  if (viewportInteraction.type === 'tap' && moved) {
    context.viewportInteractionRef.current = {
      type: 'pan',
      pointerId: event.pointerId,
      startPan: { ...context.pan },
      startClient: { x: event.clientX, y: event.clientY },
    };
    context.onSelectedRectChange(null);
  }

  const activeViewportInteraction = context.viewportInteractionRef.current;

  if (activeViewportInteraction?.type === 'pan' && activeViewportInteraction.pointerId === event.pointerId) {
    const panDx = event.clientX - activeViewportInteraction.startClient.x;
    const panDy = event.clientY - activeViewportInteraction.startClient.y;

    context.setPan(context.clampPan({
      x: activeViewportInteraction.startPan.x + panDx,
      y: activeViewportInteraction.startPan.y + panDy,
    }, context.scale));
  } else if (viewportInteraction.type === 'tap') {
    context.viewportInteractionRef.current = {
      ...viewportInteraction,
      moved,
    };
  }

  return true;
}

type RectMoveContext = {
  rectInteractionRef: { current: RectInteraction | null };
  getImageBounds: () => DOMRect | null;
  getImageFractionFromClient: (clientX: number, clientY: number) => { x: number; y: number };
  updateRect: (rectId: string, updater: (rect: RedactionRect) => RedactionRect) => void;
};

function moveRectInteraction(event: React.PointerEvent<HTMLElement>, context: RectMoveContext): void {
  const interaction = context.rectInteractionRef.current;

  if (!interaction || !context.getImageBounds()) {
    return;
  }

  const {
x,
y
} = context.getImageFractionFromClient(event.clientX, event.clientY);

  if (interaction.type === 'drag') {
    const dx = x - interaction.startX;
    const dy = y - interaction.startY;

    context.updateRect(interaction.rectId, (rect) => clampRect({
      ...rect,
      x: interaction.rect.x + dx,
      y: interaction.rect.y + dy,
    }));

    return;
  }

  context.updateRect(interaction.rectId, (rect) => resizeRect(
    rect,
    interaction.handle,
    x,
    y,
    interaction.rect,
    interaction.startX,
    interaction.startY,
  ));
}

const MOBILE_BREAKPOINT_PX = 768;
const DESKTOP_HANDLE_TOUCH_PX = 44;
const MOBILE_HANDLE_TOUCH_PX = 56;
const DESKTOP_HANDLE_DOT_PX = 16;
const MOBILE_HANDLE_DOT_PX = 16;
const DESKTOP_DELETE_BUTTON_PX = 44;
const MOBILE_DELETE_BUTTON_PX = 44;
const DELETE_BUTTON_GAP_PX = 8;

function useLargeTouchTargets(): boolean {
  const [largeTouch, setLargeTouch] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`).matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`);
    const onChange = () => setLargeTouch(mediaQuery.matches);

    onChange();
    mediaQuery.addEventListener('change', onChange);

    return () => mediaQuery.removeEventListener('change', onChange);
  }, []);

  return largeTouch;
}

function getHandlePosition(handle: ResizeHandle, offset: number): React.CSSProperties {
  switch (handle) {
    case 'nw':
      return { top: -offset, left: -offset, cursor: 'nwse-resize' };
    case 'ne':
      return { top: -offset, right: -offset, cursor: 'nesw-resize' };
    case 'sw':
      return { bottom: -offset, left: -offset, cursor: 'nesw-resize' };
    case 'se':
      return { bottom: -offset, right: -offset, cursor: 'nwse-resize' };
  }
}

type ResizeHandleButtonProps = {
  handle: ResizeHandle;
  ariaLabel: string;
  touchSize: number;
  dotSize: number;
  onPointerDown: (event: React.PointerEvent, handle: ResizeHandle) => void;
  onKeyDown: (event: React.KeyboardEvent, handle: ResizeHandle) => void;
};

function ResizeHandleButton({
  handle,
  ariaLabel,
  touchSize,
  dotSize,
  onPointerDown,
  onKeyDown,
}: ResizeHandleButtonProps) {
  const offset = touchSize / 2;

  const controlButtonStyle: React.CSSProperties = {
    padding: 0,
    border: 'none',
    background: 'transparent',
    boxSizing: 'border-box',
  };

  return (
    <button
      type="button"
      aria-label={ariaLabel}
      className="absolute z-20 flex items-center justify-center"
      style={{
        ...controlButtonStyle,
        width: touchSize,
        height: touchSize,
        ...getHandlePosition(handle, offset),
      }}
      onPointerDown={(event) => onPointerDown(event, handle)}
      onKeyDown={(event) => onKeyDown(event, handle)}
    >
      <span
        className="rounded-full bg-[white]"
        style={{
          width: dotSize,
          height: dotSize,
          boxShadow: '0 0 0 2px rgba(0, 0, 0, 0.5)',
        }}
      />
    </button>
  );
}

export const RedactionEditor = forwardRef<RedactionEditorHandle, RedactionEditorProps>(function RedactionEditor({
  file,
  rectangles,
  onRectanglesChange,
  selectedRectId,
  onSelectedRectChange,
  onZoomStateChange,
  texts,
}, ref) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const rectInteractionRef = useRef<RectInteraction | null>(null);
  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const viewportInteractionRef = useRef<ViewportInteraction | null>(null);
  const pinchStartRef = useRef<PinchStart | null>(null);
  const pendingRectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rectanglesRef = useRef(rectangles);

  useEffect(() => {
    rectanglesRef.current = rectangles;
  }, [rectangles]);

  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [overlaySize, setOverlaySize] = useState({ width: 0, height: 0 });
  const [wrapperSize, setWrapperSize] = useState({ width: 0, height: 0 });
  const [scale, setScale] = useState(MIN_SCALE);
  const [pan, setPan] = useState<PanState>({ x: 0, y: 0 });
  const [isAnimatingZoom, setIsAnimatingZoom] = useState(false);
  const largeTouchTargets = useLargeTouchTargets();
  const handleTouchSize = largeTouchTargets ? MOBILE_HANDLE_TOUCH_PX : DESKTOP_HANDLE_TOUCH_PX;
  const handleDotSize = largeTouchTargets ? MOBILE_HANDLE_DOT_PX : DESKTOP_HANDLE_DOT_PX;
  const deleteButtonSize = largeTouchTargets ? MOBILE_DELETE_BUTTON_PX : DESKTOP_DELETE_BUTTON_PX;

  useEffect(() => {
    let cancelled = false;

    async function loadImage() {
      try {
        const base64 = await reader(file);

        if (!cancelled) {
          setImageSrc(base64);
        }
      } catch (error) {
        console.error(error);
      }
    }

    loadImage();

    return () => {
      cancelled = true;
    };
  }, [file]);

  useEffect(() => {
    setScale(MIN_SCALE);
    setPan({ x: 0, y: 0 });
    pointersRef.current.clear();
    viewportInteractionRef.current = null;
    pinchStartRef.current = null;

    if (pendingRectTimeoutRef.current) {
      clearTimeout(pendingRectTimeoutRef.current);
      pendingRectTimeoutRef.current = null;
    }
  }, [imageSrc]);

  useEffect(() => () => {
    if (pendingRectTimeoutRef.current) {
      clearTimeout(pendingRectTimeoutRef.current);
    }
  }, []);

  const contentOffset = {
    x: Math.max(0, (wrapperSize.width - overlaySize.width) / 2),
    y: Math.max(0, (wrapperSize.height - overlaySize.height) / 2),
  };

  const clampPan = useCallback((
    nextPan: PanState,
    nextScale: number,
    size = overlaySize,
    wrapper = wrapperSize,
  ): PanState => {
    if (nextScale <= MIN_SCALE) {
      return { x: 0, y: 0 };
    }

    const offsetX = Math.max(0, (wrapper.width - size.width) / 2);
    const offsetY = Math.max(0, (wrapper.height - size.height) / 2);
    const scaledWidth = size.width * nextScale;
    const scaledHeight = size.height * nextScale;

    const minPanX = wrapper.width - offsetX - scaledWidth;
    const maxPanX = -offsetX;
    const minPanY = wrapper.height - offsetY - scaledHeight;
    const maxPanY = -offsetY;

    return {
      x: Math.max(minPanX, Math.min(maxPanX, nextPan.x)),
      y: Math.max(minPanY, Math.min(maxPanY, nextPan.y)),
    };
  }, [overlaySize, wrapperSize]);

  const applyZoomAtPoint = useCallback((
    nextScale: number,
    anchorX: number,
    anchorY: number,
    currentScale = scale,
    currentPan = pan,
    animate = false,
  ) => {
    const clampedScale = clampScale(nextScale);
    const imageX = (anchorX - contentOffset.x - currentPan.x) / currentScale;
    const imageY = (anchorY - contentOffset.y - currentPan.y) / currentScale;

    const nextPan = clampPan({
      x: anchorX - contentOffset.x - imageX * clampedScale,
      y: anchorY - contentOffset.y - imageY * clampedScale,
    }, clampedScale);

    if (animate) {
      setIsAnimatingZoom(true);
    }

    setScale(clampedScale);
    setPan(nextPan);
    onSelectedRectChange(null);
  }, [clampPan, contentOffset.x, contentOffset.y, onSelectedRectChange, pan, scale]);

  const zoomBy = useCallback((factor: number) => {
    const wrapper = wrapperRef.current;

    if (!wrapper) {
      return;
    }

    const anchorX = wrapper.clientWidth / 2;
    const anchorY = wrapper.clientHeight / 2;

    applyZoomAtPoint(scale * factor, anchorX, anchorY, scale, pan, true);
  }, [applyZoomAtPoint, pan, scale]);

  useImperativeHandle(ref, () => ({
    zoomIn: () => zoomBy(ZOOM_STEP),
    zoomOut: () => zoomBy(1 / ZOOM_STEP),
  }), [zoomBy]);

  useEffect(() => {
    onZoomStateChange?.({
      canZoomIn: scale < MAX_SCALE,
      canZoomOut: scale > MIN_SCALE,
    });
  }, [onZoomStateChange, scale]);

  const updateOverlaySize = useCallback(() => {
    const img = imageRef.current;

    if (!img) {
      return;
    }

    setOverlaySize({ width: img.clientWidth, height: img.clientHeight });
  }, []);

  const getImageBounds = useCallback((): DOMRect | null => {
    const img = imageRef.current;

    if (!img) {
      return null;
    }

    return getRenderedImageBounds(img);
  }, []);

  const getImageFractionFromClient = useCallback((clientX: number, clientY: number) => {
    const bounds = getImageBounds();

    if (!bounds) {
      return { x: 0, y: 0 };
    }

    return clientPointToImageFractionClamped(clientX, clientY, bounds);
  }, [getImageBounds]);

  const isClientPointInsideImage = useCallback((clientX: number, clientY: number) => {
    const bounds = getImageBounds();

    if (!bounds) {
      return false;
    }

    return clientPointToImageFraction(clientX, clientY, bounds) !== null;
  }, [getImageBounds]);

  const updateWrapperSize = useCallback(() => {
    const wrapper = wrapperRef.current;

    if (!wrapper) {
      return;
    }

    setWrapperSize({
      width: wrapper.clientWidth,
      height: wrapper.clientHeight,
    });
  }, []);

  useEffect(() => {
    const wrapper = wrapperRef.current;

    if (!wrapper) {
      return;
    }

    const handleResize = () => {
      updateWrapperSize();
      updateOverlaySize();
    };

    handleResize();

    const observer = new ResizeObserver(handleResize);

    observer.observe(wrapper);

    return () => observer.disconnect();
  }, [imageSrc, updateOverlaySize, updateWrapperSize]);

  useEffect(() => {
    setPan((currentPan) => clampPan(currentPan, scale));
  }, [clampPan, scale, overlaySize, wrapperSize]);

  const updateRect = useCallback((rectId: string, updater: (rect: RedactionRect) => RedactionRect) => {
    onRectanglesChange(
      rectangles.map((rect) => (rect.id === rectId ? clampRect(updater(rect)) : rect)),
    );
  }, [onRectanglesChange, rectangles]);

  const removeRect = useCallback((rectId: string) => {
    onRectanglesChange(rectangles.filter((rect) => rect.id !== rectId));
    onSelectedRectChange(null);
  }, [onRectanglesChange, onSelectedRectChange, rectangles]);

  const createRectAtPoint = useCallback((clientX: number, clientY: number) => {
    const bounds = getImageBounds();

    if (!bounds) {
      return;
    }

    const point = clientPointToImageFraction(clientX, clientY, bounds);

    if (!point) {
      return;
    }

    const {
x,
y
} = point;

    const minFracW = Math.max(DEFAULT_RECT_WIDTH, bounds.width > 0 ? MIN_TOUCH_PX / bounds.width : 0);
    const minFracH = Math.max(DEFAULT_RECT_HEIGHT, bounds.height > 0 ? MIN_TOUCH_PX / bounds.height : 0);
    const newRect = createRedactionRect(x, y, minFracW, minFracH);

    onRectanglesChange([...rectangles, newRect]);
    onSelectedRectChange(newRect.id);
  }, [getImageBounds, onRectanglesChange, onSelectedRectChange, rectangles]);

  const cancelPendingRect = useCallback(() => {
    if (pendingRectTimeoutRef.current) {
      clearTimeout(pendingRectTimeoutRef.current);
      pendingRectTimeoutRef.current = null;
    }
  }, []);

  const scheduleRectAtPoint = useCallback((clientX: number, clientY: number) => {
    cancelPendingRect();

    pendingRectTimeoutRef.current = setTimeout(() => {
      pendingRectTimeoutRef.current = null;

      if (pointersRef.current.size > 0 || pinchStartRef.current) {
        return;
      }

      createRectAtPoint(clientX, clientY);
    }, PINCH_GRACE_MS);
  }, [cancelPendingRect, createRectAtPoint]);

  const updateDrawRect = useCallback((
    startClient: { x: number; y: number },
    currentClient: { x: number; y: number },
    rectId: string | null,
  ): string => {
    const bounds = getImageBounds();

    if (!bounds) {
      return rectId ?? '';
    }

    const start = clientPointToImageFractionClamped(startClient.x, startClient.y, bounds);
    const current = clientPointToImageFractionClamped(currentClient.x, currentClient.y, bounds);
    const dimensions = rectFromCorners(start.x, start.y, current.x, current.y);

    if (!rectId) {
      const newRect = clampRect({
        id: crypto.randomUUID(),
        ...dimensions,
      });

      onRectanglesChange([...rectanglesRef.current, newRect]);
      onSelectedRectChange(newRect.id);

      return newRect.id;
    }

    onRectanglesChange(
      rectanglesRef.current.map((rect) => (
        rect.id === rectId ? clampRect({ ...rect, ...dimensions }) : rect
      )),
    );
    onSelectedRectChange(rectId);

    return rectId;
  }, [getImageBounds, onRectanglesChange, onSelectedRectChange]);

  const startPinch = useCallback(() => {
    const pointers = Array.from(pointersRef.current.values());

    if (pointers.length < 2) {
      return;
    }

    cancelPendingRect();
    viewportInteractionRef.current = null;
    rectInteractionRef.current = null;
    onSelectedRectChange(null);

    pinchStartRef.current = {
      distance: getDistance(pointers[0], pointers[1]),
      scale,
      pan: { ...pan },
      midpoint: {
        x: (pointers[0].x + pointers[1].x) / 2,
        y: (pointers[0].y + pointers[1].y) / 2,
      },
    };
  }, [cancelPendingRect, onSelectedRectChange, pan, scale]);

  const updatePinch = useCallback(() => {
    const pinchStart = pinchStartRef.current;
    const pointers = Array.from(pointersRef.current.values());

    if (!pinchStart || pointers.length < 2 || pinchStart.distance === 0) {
      return;
    }

    const distance = getDistance(pointers[0], pointers[1]);
    const nextScale = clampScale(pinchStart.scale * (distance / pinchStart.distance));

    const midpoint = {
      x: (pointers[0].x + pointers[1].x) / 2,
      y: (pointers[0].y + pointers[1].y) / 2,
    };

    const imageX = (pinchStart.midpoint.x - contentOffset.x - pinchStart.pan.x) / pinchStart.scale;
    const imageY = (pinchStart.midpoint.y - contentOffset.y - pinchStart.pan.y) / pinchStart.scale;

    const nextPan = clampPan({
      x: midpoint.x - contentOffset.x - imageX * nextScale,
      y: midpoint.y - contentOffset.y - imageY * nextScale,
    }, nextScale);

    setScale(nextScale);
    setPan(nextPan);
  }, [clampPan, contentOffset.x, contentOffset.y]);

  const handleOverlayPointerDown = (event: React.PointerEvent<HTMLElement>) => {
    if (event.target !== overlayRef.current) {
      return;
    }

    event.preventDefault();

    if (!isClientPointInsideImage(event.clientX, event.clientY)) {
      return;
    }

    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pointersRef.current.size >= 2) {
      cancelPendingRect();
      startPinch();

      return;
    }

    cancelPendingRect();

    const canDrawRectWithDrag = scale <= MIN_SCALE;

    if (canDrawRectWithDrag) {
      viewportInteractionRef.current = {
        type: 'draw',
        pointerId: event.pointerId,
        startClient: { x: event.clientX, y: event.clientY },
        rectId: null,
        moved: false,
      };
      overlayRef.current?.setPointerCapture(event.pointerId);

      return;
    }

    viewportInteractionRef.current = {
      type: 'tap',
      pointerId: event.pointerId,
      startClient: { x: event.clientX, y: event.clientY },
      moved: false,
    };
    overlayRef.current?.setPointerCapture(event.pointerId);
  };

  const handleRectPointerDown = (event: React.PointerEvent, rect: RedactionRect) => {
    event.stopPropagation();
    onSelectedRectChange(rect.id);

    const {
x,
y
} = getImageFractionFromClient(event.clientX, event.clientY);

    rectInteractionRef.current = {
      type: 'drag',
      rectId: rect.id,
      startX: x,
      startY: y,
      rect,
    };

    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleResizePointerDown = (
    event: React.PointerEvent,
    rect: RedactionRect,
    handle: ResizeHandle,
  ) => {
    event.stopPropagation();
    onSelectedRectChange(rect.id);

    if (!getImageBounds()) {
      return;
    }

    const {
x,
y
} = getImageFractionFromClient(event.clientX, event.clientY);

    rectInteractionRef.current = {
      type: 'resize',
      rectId: rect.id,
      handle,
      startX: x,
      startY: y,
      rect,
    };

    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleOverlayPointerMove = (event: React.PointerEvent<HTMLElement>) => {
    trackActivePointer(pointersRef.current, event);

    if (pinchPointers(pointersRef.current, pinchStartRef.current, startPinch, updatePinch)) {
      return;
    }

    if (moveDrawViewport(event, viewportInteractionRef, updateDrawRect)) {
      return;
    }

    if (movePanOrTapViewport(event, {
      viewportInteractionRef,
      pan,
      scale,
      setPan,
      clampPan,
      onSelectedRectChange,
    })) {
      return;
    }

    moveRectInteraction(event, {
      rectInteractionRef,
      getImageBounds,
      getImageFractionFromClient,
      updateRect,
    });
  };

  const handleOverlayPointerUp = (event: React.PointerEvent<HTMLElement>) => {
    pointersRef.current.delete(event.pointerId);

    if (pointersRef.current.size < 2) {
      pinchStartRef.current = null;
    }

    const viewportInteraction = viewportInteractionRef.current;

    if (viewportInteraction?.pointerId === event.pointerId && viewportInteraction.type === 'draw') {
      if (!viewportInteraction.moved) {
        createRectAtPoint(viewportInteraction.startClient.x, viewportInteraction.startClient.y);
      }

      viewportInteractionRef.current = null;

      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        // Pointer may not have capture when released from overlay.
      }

      return;
    }

    if (
      viewportInteraction?.pointerId === event.pointerId
      && viewportInteraction.type === 'tap'
      && !viewportInteraction.moved
      && !pinchStartRef.current
      && isClientPointInsideImage(viewportInteraction.startClient.x, viewportInteraction.startClient.y)
    ) {
      scheduleRectAtPoint(viewportInteraction.startClient.x, viewportInteraction.startClient.y);
    }

    if (viewportInteraction?.pointerId === event.pointerId) {
      viewportInteractionRef.current = null;
    }

    if (rectInteractionRef.current) {
      rectInteractionRef.current = null;

      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        // Pointer may not have capture when released from overlay.
      }
    }
  };

  const handleRectKeyDown = (event: React.KeyboardEvent, rect: RedactionRect) => {
    if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      removeRect(rect.id);

      return;
    }

    const step = KEYBOARD_MOVE_STEP;

    if (event.shiftKey) {
      let handle: ResizeHandle = 'se';
      let dx = 0;
      let dy = 0;

      switch (event.key) {
        case 'ArrowLeft':
          handle = 'sw';
          dx = -step;
          break;
        case 'ArrowRight':
          handle = 'se';
          dx = step;
          break;
        case 'ArrowUp':
          handle = 'nw';
          dy = -step;
          break;
        case 'ArrowDown':
          handle = 'se';
          dy = step;
          break;
        default:
          return;
      }

      event.preventDefault();
      onSelectedRectChange(rect.id);
      updateRect(rect.id, (current) => applyResizeDelta(current, handle, dx, dy));

      return;
    }

    let dx = 0;
    let dy = 0;

    switch (event.key) {
      case 'ArrowLeft':
        dx = -step;
        break;
      case 'ArrowRight':
        dx = step;
        break;
      case 'ArrowUp':
        dy = -step;
        break;
      case 'ArrowDown':
        dy = step;
        break;
      default:
        return;
    }

    event.preventDefault();
    onSelectedRectChange(rect.id);
    updateRect(rect.id, (current) => clampRect({
      ...current,
      x: current.x + dx,
      y: current.y + dy,
    }));
  };

  const handleResizeHandleKeyDown = (
    event: React.KeyboardEvent,
    rect: RedactionRect,
    handle: ResizeHandle,
  ) => {
    const step = KEYBOARD_MOVE_STEP;
    let dx = 0;
    let dy = 0;

    switch (event.key) {
      case 'ArrowLeft':
        dx = -step;
        break;
      case 'ArrowRight':
        dx = step;
        break;
      case 'ArrowUp':
        dy = -step;
        break;
      case 'ArrowDown':
        dy = step;
        break;
      default:
        return;
    }

    event.preventDefault();
    event.stopPropagation();
    onSelectedRectChange(rect.id);
    updateRect(rect.id, (current) => applyResizeDelta(current, handle, dx, dy));
  };

  if (!imageSrc) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <span className="sr-only">{texts.imageLoading}</span>
      </div>
    );
  }

  const derivedOverlayLeft = contentOffset.x + pan.x;
  const derivedOverlayTop = contentOffset.y + pan.y;
  const scaledOverlayWidth = overlaySize.width * scale;
  const scaledOverlayHeight = overlaySize.height * scale;

  return (
    <div
      ref={wrapperRef}
      className={clsx(
        'relative h-full w-full select-none',
        scale <= MIN_SCALE ? 'overflow-visible' : 'overflow-hidden',
      )}
      style={{ WebkitUserSelect: 'none' }}
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="absolute leading-none"
          style={{
            left: contentOffset.x,
            top: contentOffset.y,
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
            transformOrigin: '0 0',
            transition: isAnimatingZoom ? 'transform 200ms ease-out' : undefined,
          }}
          onTransitionEnd={(event) => {
            if (event.propertyName === 'transform' && isAnimatingZoom) {
              setIsAnimatingZoom(false);
            }
          }}
        >
          <img
            ref={imageRef}
            src={imageSrc}
            alt={texts.editImageAlt({ fileName: file.name })}
            className="block max-h-full max-w-full object-contain"
            style={{
              maxHeight: wrapperSize.height || '100%',
              maxWidth: wrapperSize.width || '100%',
            }}
            draggable={false}
            onLoad={updateOverlaySize}
          />
        </div>
      </div>

      {overlaySize.width > 0 && overlaySize.height > 0 && (
        <div
          ref={overlayRef}
          role="application"
          aria-label={texts.overlayAriaLabel}
          className={clsx(
            'absolute touch-none select-none',
            scale <= MIN_SCALE ? 'overflow-visible' : 'overflow-hidden',
          )}
          style={{
            left: derivedOverlayLeft,
            top: derivedOverlayTop,
            width: scaledOverlayWidth,
            height: scaledOverlayHeight,
            touchAction: 'none',
            pointerEvents: 'auto',
            transition: isAnimatingZoom
              ? 'left 200ms ease-out, top 200ms ease-out, width 200ms ease-out, height 200ms ease-out'
              : undefined,
          }}
          onPointerDown={handleOverlayPointerDown}
          onPointerMove={handleOverlayPointerMove}
          onPointerUp={handleOverlayPointerUp}
          onPointerCancel={handleOverlayPointerUp}
        >
            {rectangles.map((rect, index) => {
              const isSelected = rect.id === selectedRectId;

              return (
                <button
                  key={rect.id}
                  type="button"
                  aria-label={
                    isSelected
                      ? texts.redactionSelectedLabel({ index })
                      : texts.redactionLabel({ index })
                  }
                  aria-keyshortcuts="ArrowUp ArrowDown ArrowLeft ArrowRight Shift+ArrowUp Shift+ArrowDown Shift+ArrowLeft Shift+ArrowRight Delete"
                  aria-pressed={isSelected}
                  className={clsx(
                    'absolute overflow-visible border-0 bg-transparent p-0 text-left font-inherit',
                    isSelected && 'outline outline-2 -outline-offset-2 outline-input-activated',
                  )}
                  style={{
                    left: `${rect.x * 100}%`,
                    top: `${rect.y * 100}%`,
                    width: `${rect.width * 100}%`,
                    height: `${rect.height * 100}%`,
                    backgroundImage:
                      'repeating-linear-gradient(-45deg, rgba(0, 0, 0, 0.4) 0, rgba(0, 0, 0, 0.4) 1vw, rgba(0, 0, 0, 0.3) 1vw, rgba(0, 0, 0, 0.3) 2vw)',
                    boxShadow: 'inset 0 0 0 3px #ffffff',
                  }}
                  onPointerDown={(event) => handleRectPointerDown(event, rect)}
                  onPointerMove={handleOverlayPointerMove}
                  onPointerUp={handleOverlayPointerUp}
                  onPointerCancel={handleOverlayPointerUp}
                  onKeyDown={(event) => handleRectKeyDown(event, rect)}
                  onFocus={() => onSelectedRectChange(rect.id)}
                >
                  {isSelected && (
                    <>
                      <button
                        type="button"
                        aria-label={texts.removeRedactionLabel({ index })}
                        className="absolute left-1/2 z-20 flex items-center justify-center rounded-full bg-[white] text-error shadow-small"
                        style={{
                          top: -(deleteButtonSize + DELETE_BUTTON_GAP_PX),
                          width: deleteButtonSize,
                          height: deleteButtonSize,
                          transform: 'translateX(-50%)',
                          padding: 0,
                          border: 'none',
                          boxSizing: 'border-box',
                        }}
                        onPointerDown={(event) => event.stopPropagation()}
                        onClick={(event) => {
                          event.stopPropagation();
                          removeRect(rect.id);
                        }}
                      >
                        <BasicBin style={{ width: 20, height: 20 }} />
                      </button>

                      {(['nw', 'ne', 'sw', 'se'] as ResizeHandle[]).map((handle) => (
                        <ResizeHandleButton
                          key={handle}
                          handle={handle}
                          ariaLabel={texts.resizeHandleLabel({ corner: handle })}
                          touchSize={handleTouchSize}
                          dotSize={handleDotSize}
                          onPointerDown={(event, resizeHandle) => handleResizePointerDown(event, rect, resizeHandle)}
                          onKeyDown={(event, resizeHandle) => handleResizeHandleKeyDown(event, rect, resizeHandle)}
                        />
                      ))}
                    </>
                  )}
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
});
